import { create } from 'zustand';
import { logger } from '@/lib/logger';

const TAG = 'RdpStore';

export interface MonitorInfo {
  id: number;
  name: string;
  width: number;
  height: number;
  x: number;
  y: number;
  scale_factor: number;
  is_primary: boolean;
}

export interface RdpStats {
  fps: number;
  bitrate: number;
  latency: number;
}

export type ConsentResult = 'allow_full' | 'allow_view_only' | 'deny';

interface ConsentRequest {
  requesterName: string;
  resolve: (result: ConsentResult) => void;
}

interface RdpState {
  // Host state
  isSharing: boolean;
  connectedViewers: string[];
  inputForwardingEnabled: boolean;
  clipboardSyncEnabled: boolean;

  // Consent
  pendingConsent: ConsentRequest | null;

  // Client/Viewer state
  isViewing: boolean;
  viewingMachineId: string | null;
  remoteScreenWidth: number;
  remoteScreenHeight: number;
  remoteScaleFactor: number;
  availableMonitors: MonitorInfo[];
  isFullscreen: boolean;
  inputEnabled: boolean;
  remoteStream: MediaStream | null;

  // Stats
  stats: RdpStats;

  // Host actions
  setSharing: (sharing: boolean) => void;
  addViewer: (viewerId: string) => void;
  removeViewer: (viewerId: string) => void;
  setInputForwarding: (enabled: boolean) => void;
  setClipboardSync: (enabled: boolean) => void;
  requestConsent: (requesterName: string) => Promise<ConsentResult>;
  resolveConsent: (result: ConsentResult) => void;

  // Client actions
  setViewing: (viewing: boolean, machineId?: string | null) => void;
  setRemoteStream: (stream: MediaStream | null) => void;
  setRemoteScreenDimensions: (width: number, height: number, scaleFactor: number) => void;
  setAvailableMonitors: (monitors: MonitorInfo[]) => void;
  setFullscreen: (fullscreen: boolean) => void;
  setInputEnabled: (enabled: boolean) => void;
  updateStats: (stats: Partial<RdpStats>) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  isSharing: false,
  connectedViewers: [] as string[],
  inputForwardingEnabled: true,
  clipboardSyncEnabled: true,
  pendingConsent: null as ConsentRequest | null,
  isViewing: false,
  viewingMachineId: null as string | null,
  remoteScreenWidth: 0,
  remoteScreenHeight: 0,
  remoteScaleFactor: 1,
  availableMonitors: [] as MonitorInfo[],
  isFullscreen: false,
  inputEnabled: true,
  remoteStream: null as MediaStream | null,
  stats: { fps: 0, bitrate: 0, latency: 0 },
};

export const useRdpStore = create<RdpState>()((set) => ({
  ...initialState,

  // Host actions
  setSharing: (sharing) => {
    logger.info(TAG, `Screen sharing ${sharing ? 'started' : 'stopped'}`);
    set({ isSharing: sharing });
  },

  addViewer: (viewerId) =>
    set((state) => ({
      connectedViewers: [...state.connectedViewers, viewerId],
    })),

  removeViewer: (viewerId) =>
    set((state) => ({
      connectedViewers: state.connectedViewers.filter((v) => v !== viewerId),
    })),

  setInputForwarding: (enabled) => set({ inputForwardingEnabled: enabled }),
  setClipboardSync: (enabled) => set({ clipboardSyncEnabled: enabled }),

  requestConsent: (requesterName) => {
    return new Promise<ConsentResult>((resolve) => {
      set({ pendingConsent: { requesterName, resolve } });
    });
  },

  resolveConsent: (result) => {
    const { pendingConsent } = useRdpStore.getState();
    if (pendingConsent) {
      pendingConsent.resolve(result);
      set({ pendingConsent: null });
    }
  },

  // Client actions
  setViewing: (viewing, machineId = null) => {
    logger.info(TAG, `Viewing ${viewing ? `machine ${machineId}` : 'stopped'}`);
    set({
      isViewing: viewing,
      viewingMachineId: viewing ? machineId : null,
      ...(viewing ? {} : { remoteStream: null, remoteScreenWidth: 0, remoteScreenHeight: 0 }),
    });
  },

  setRemoteStream: (stream) => set({ remoteStream: stream }),

  setRemoteScreenDimensions: (width, height, scaleFactor) =>
    set({ remoteScreenWidth: width, remoteScreenHeight: height, remoteScaleFactor: scaleFactor }),

  setAvailableMonitors: (monitors) => set({ availableMonitors: monitors }),
  setFullscreen: (fullscreen) => set({ isFullscreen: fullscreen }),
  setInputEnabled: (enabled) => set({ inputEnabled: enabled }),

  updateStats: (stats) =>
    set((state) => ({ stats: { ...state.stats, ...stats } })),

  reset: () => set(initialState),
}));
